import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { useForm, Controller } from 'react-hook-form';
import { formSchema } from './schema';
import { zodResolver } from '@hookform/resolvers/zod';
import { FiEye, FiEyeOff, FiCheckCircle, FiX, FiAlertCircle } from 'react-icons/fi';
import { API_BASE } from '../../services/apiClient';

// Tipos de perfil (reintroduzido 'particular' apenas nesta página)
const PERFIS = ['profissional', 'empreendimento', 'particular'];

const PageContainer = styled.div`
  max-width: 1320px;
  margin: 0 auto;
  /* Afastar do topo (considerando header fixo) */
  padding: 120px 16px 64px;

  @media (max-width: 1024px) {
    padding-top: 108px;
  }

  @media (max-width: 768px) {
    padding-top: 96px;
  }
`;

// AlertBoxError será definido após AlertBox para evitar referência antes da inicialização

const Card = styled.div`
  background: #fff;
  border: 1px solid #eee;
  border-radius: 12px;
  box-shadow: 0 10px 30px rgba(0,0,0,0.04);
`;

const CardHeader = styled.div`
  padding: 20px 20px 0 20px;
`;

const Title = styled.h1`
  margin: 0 auto;
  font-size: 1.1rem;
  color: #ffffff;
  background: #BA824F;
  border-radius: 10px;
  padding: 10px 18px;
  width: fit-content;
  font-weight: 600;
  text-align: center;
`;

const Tabs = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
  margin-top: 12px;
`;

const TabButton = styled.button`
  padding: 10px 12px;
  border: 1px solid #e5e7eb;
  background: ${props => (props.$active ? '#fff' : '#fafafa')};
  border-radius: 8px;
  cursor: pointer;
  font-weight: 600;
  color: #162132;
  outline: ${props => (props.$active ? '2px solid #B07D54' : 'none')};
  box-shadow: ${props => (props.$active ? '0 0 0 2px #B07D5422' : 'none')};
`;

const CardContent = styled.div`
  padding: 20px;
`;

const Section = styled.section`
  margin-bottom: 24px;
`;

const SectionTitle = styled.h3`
  font-size: 1.05rem;
  color: #162132;
  margin: 0 0 12px 0;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 12px;
  @media (min-width: 768px) {
    grid-template-columns: 1fr 1fr;
  }
`;

const InputGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Label = styled.label`
  font-size: 0.9rem;
  color: #162132;
`;

const Input = styled.input`
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 10px 12px;
  font-size: 0.95rem;
  width: 100%;
  /* espaço para o botão olho quando houver */
  padding-right: 40px;
`;

const Select = styled.select`
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 10px 12px;
  font-size: 0.95rem;
  background: #fff;
`;

const PasswordWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;
`;

const TogglePasswordButton = styled.button`
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  padding: 4px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #6b7280;
`;

const SwitchRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  border: 1px solid #f1f1f1;
  border-radius: 10px;
  padding: 10px 12px;
  
  /* Aumenta o tamanho visual dos checkboxes dentro deste row */
  & input[type="checkbox"] {
    width: 22px;
    height: 22px;
    cursor: pointer;
    accent-color: #BA824F; /* cor coerente com a marca */
  }
`;

const ErrorText = styled.p`
  color: #dc3545;
  margin: 4px 0 0 0;
  font-size: 0.8rem;
`;

const SubmitButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 12px 24px;
  background: linear-gradient(90deg, #C79264 0%, #BA824F 50%, #8A5A32 100%);
  color: #fff;
  border: none;
  border-radius: 12px;
  font-weight: 700;
  font-size: 1rem;
  cursor: pointer;
  margin: 12px 0 0 0;
  box-shadow: 0 2px 6px rgba(0,0,0,0.08);
  transition: filter .2s ease, transform .05s ease-in-out;

  &:hover { filter: brightness(0.97); }
  &:active { transform: translateY(1px); }
  &:disabled { opacity: 0.7; cursor: not-allowed; }
`;

// Bloco de termos
const TermsContainer = styled.div`
  display: grid;
  gap: 8px;
  color: #374151;
  font-size: 0.9rem;
`;

const TermsLink = styled.a`
  color: #1f2937;
  text-decoration: underline;
  font-weight: 600;
`;

// Painel cinza (como no segundo print)
const TermsPanel = styled.div`
  background: ${props => (props.$hasError ? '#fef2f2' : '#f8fafc')};
  border: 1px solid ${props => (props.$hasError ? '#fca5a5' : '#e5e7eb')};
  border-radius: 10px;
  padding: 16px;
  display: grid;
  gap: 12px;
`;

// Linha com checkbox dentro do painel
const TermsCheckboxPanel = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  background: #ffffff;
  border: 1px solid #eceff1;
  border-radius: 10px;
  padding: 12px 14px;
  gap: 12px;

  & input[type='checkbox'] {
    width: 20px;
    height: 20px;
    cursor: pointer;
    accent-color: #BA824F;
  }

  small {
    display: block;
    font-size: 0.8rem;
    color: #6b7280;
    margin-top: 2px;
  }
`;

// Aviso verde (nota ambiental)
const NoticePanel = styled.div`
  background: #ecfdf5;
  border-left: 4px solid #10b981;
  color: #065f46;
  border-radius: 8px;
  padding: 12px 14px;
  margin-top: 12px;
`;

const SmallNote = styled.p`
  font-size: 0.8rem;
  color: #6b7280;
  margin: 8px 0 0 0;
`;

// Alerta bonito para feedback de envio
const AlertBox = styled.div`
  display: flex;
  gap: 10px;
  align-items: flex-start;
  padding: 12px 14px;
  border-radius: 10px;
  background: #ecfdf5;
  border: 1px solid #a7f3d0;
  color: #065f46;
  box-shadow: 0 6px 18px rgba(0,0,0,0.05);
  position: relative;
  overflow: hidden;
`;

const AlertIcon = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: #d1fae5;
  color: #065f46;
  flex: 0 0 28px;
`;

// Variante para erro (usada no alerta de erro)
const AlertIconError = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: #fee2e2;
  color: #991b1b;
  flex: 0 0 28px;
`;

const AlertContent = styled.div`
  display: grid;
  gap: 2px;
`;

const AlertTitle = styled.div`
  font-weight: 700;
`;

const AlertDesc = styled.div`
  font-size: 0.9rem;
  opacity: 0.9;
`;

const AlertClose = styled.button`
  position: absolute;
  right: 8px;
  top: 8px;
  background: transparent;
  border: none;
  color: #065f46;
  cursor: pointer;
  padding: 4px;
  border-radius: 6px;
  &:hover { background: #d1fae5; }
`;

// Agora que AlertBox já está definido, podemos criar a variante de erro
const AlertBoxError = styled(AlertBox)`
  background: #fef2f2;
  border-color: #fecaca;
  color: #991b1b;
`;

// Modal de sucesso centralizado
const SuccessOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.45);
  backdrop-filter: blur(2px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 16px;
`;

const SuccessCard = styled.div`
  width: 100%;
  max-width: 520px;
  background: #ffffff;
  border-radius: 16px;
  border: 1px solid #e5e7eb;
  box-shadow: 0 25px 60px rgba(0,0,0,0.18);
  padding: 28px 24px;
  text-align: center;
  position: relative;
  animation: popIn 240ms ease-out;

  @keyframes popIn {
    from { transform: translateY(8px) scale(0.98); opacity: 0; }
    to { transform: translateY(0) scale(1); opacity: 1; }
  }
`;

const SuccessIconWrap = styled.div`
  width: 72px;
  height: 72px;
  border-radius: 50%;
  margin: 6px auto 14px auto;
  display: grid;
  place-items: center;
  color: #0f5132;
  background: radial-gradient(closest-side, #d1fae5, #ecfdf5);
  border: 1px solid #a7f3d0;
`;

const SuccessTitle = styled.h2`
  margin: 0 0 6px 0;
  color: #0f172a;
  font-size: 1.35rem;
`;

const SuccessDesc = styled.p`
  margin: 0;
  color: #334155;
  font-size: 0.98rem;
`;

const SuccessActions = styled.div`
  display: flex;
  gap: 10px;
  justify-content: center;
  margin-top: 18px;
`;

const SuccessButton = styled.button`
  padding: 10px 14px;
  border-radius: 10px;
  border: 1px solid #e5e7eb;
  background: #BA824F;
  color: #fff;
  font-weight: 600;
  cursor: pointer;
  transition: filter .2s ease;
  &:hover { filter: brightness(0.96); }
`;

export default function CadastroHostdPage() {
  const DEBUG = typeof import.meta !== 'undefined' && import.meta?.env?.DEV;
  const location = useLocation();
  const [perfil, setPerfil] = useState('profissional');
  const [perfilLock, setPerfilLock] = useState(null); // quando definido via query, esconde tabs e trava o perfil
  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordConfirm, setShowPasswordConfirm] = useState(false);
  const [countries, setCountries] = useState([]);
  const [isencoesFiscais, setIsencoesFiscais] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');
  const [submitError, setSubmitError] = useState('');
  const [showEmailError, setShowEmailError] = useState(false);

  const tabsToShow = perfilLock ? [perfilLock] : PERFIS;

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    reset,
    setError,
    setFocus,
    control,
    clearErrors,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(formSchema),
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
    criteriaMode: 'firstError',
    shouldFocusError: true,
    defaultValues: {
      perfil: 'profissional',
      nome: '',
      sobrenome: '',
      email: '',
      senha: '',
      senhaConfirmacao: '',
      nif: '',
      isencaoUSUARIO: '',
      isentoIVA: 'nao',
      enderecoFiscal: { rua: '', codigoPostal: '', freguesia: '', pais: '' },
      telefone: '',
      telefoneConfirmacao: '',
      emailContato: '',
      aceiteTermos: false,
      designacaoFiscal: '',
      numeroLicenca: '',
      possuiEnderecoCorrespondencia: false,
      enderecoCorrespondencia: { rua: '', codigoPostal: '', freguesia: '' },
      contatoFuncionariosDiferente: false,
      telefoneFuncionarios: '',
      emailFuncionarios: '',
    },
  });

  // Watches auxiliares
  const possuiEnderecoCorrespondencia = watch('possuiEnderecoCorrespondencia');
  const contatoFuncionariosDiferente = watch('contatoFuncionariosDiferente');
  const paisSelecionado = watch('enderecoFiscal.pais');
  
  // Label dinâmico para NIF baseado no país
  const nifLabel = paisSelecionado === '1' ? 'NIF' : 'Documento Fiscal do seu país (NIF, CPF ou PASS ID)';

  const isentoIVA = watch('isentoIVA');
  useEffect(() => {
    if (isentoIVA !== 'sim') setValue('isencaoUSUARIO', '', { shouldValidate: true });
  }, [isentoIVA, setValue]);

  // Ao trocar o país, o campo muda entre lista (Portugal) e texto livre — limpa o valor anterior
  useEffect(() => {
    setValue('isencaoUSUARIO', '', { shouldValidate: false });
  }, [paisSelecionado, setValue]);

  useEffect(() => {
    const url = `${API_BASE}/paises`;

    fetch(url)
      .then(async (r) => {
        if (!r.ok) {
          throw new Error(`HTTP ${r.status}`);
        }
        return r.json();
      })
      .then((data) => {
        if (Array.isArray(data) && data.length > 0) {
          setCountries(data);
        } else {
          setCountries([]);
        setSubmitError('Não foi possível carregar os países. Atualize a página antes de continuar.');
        }
      })
      .catch(() => {
        setCountries([]);
          setSubmitError('Não foi possível carregar os países. Atualize a página antes de continuar.');
      });
  }, []);

  // Busca o catálogo oficial para a declaração de isenção da compra.
  useEffect(() => {
    fetch(`${API_BASE}/isencoes-fiscais/ativas`)
      .then(async (r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then((data) => {
        if (Array.isArray(data)) {
          setIsencoesFiscais(data);
        }
      })
      .catch((err) => {
        console.error('Erro ao buscar isenções fiscais:', err);
      });
  }, []);

  // Auto-ocultar mensagens após alguns segundos
  // Lê query param ?tipo=profissional|empreendimento|particular (fallback para ?perfil) para travar um perfil específico
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const qp = (params.get('tipo') || params.get('perfil') || '').toLowerCase();
    if (PERFIS.includes(qp)) {
      setPerfil(qp);
      setPerfilLock(qp);
      setValue('perfil', qp, { shouldValidate: true });
    } else {
      setPerfilLock(null);
      setValue('perfil', 'profissional', { shouldValidate: false });
    }
  }, [location.search, setValue]);

  useEffect(() => {
    if (submitMessage) {
      const t = setTimeout(() => setSubmitMessage(''), 10000);
      return () => clearTimeout(t);
    }
  }, [submitMessage]);

  useEffect(() => {
    if (submitError) {
      const t = setTimeout(() => setSubmitError(''), 6000);
      return () => clearTimeout(t);
    }
  }, [submitError]);

  const onChangePerfil = (p) => {
    setPerfil(p);
    setValue('perfil', p, { shouldValidate: true });
  };

  const onSubmit = async (data) => {
    setSubmitting(true);
    setSubmitMessage('');
    setSubmitError('');

    // telefone de confirmação é opcional; não bloquear se diferente ou vazio

    // Mapeia dados do formulário unificado para o payload esperado pela API antiga
    const fiscalRua = data.enderecoFiscal.rua;
    const fiscalCP = data.enderecoFiscal.codigoPostal;
    const fiscalFreg = data.enderecoFiscal.freguesia;
    const fiscalPais = data.enderecoFiscal.pais;

    // No formulário unificado atual não há campos separados de correspondência/sede,
    // portanto replicamos o endereço fiscal nesses campos.
    // Se os switches estiverem ligados, usar os campos específicos; caso contrário, replicar o fiscal
    const rua1 = (data.possuiEnderecoCorrespondencia ? data.enderecoCorrespondencia?.rua : fiscalRua) || fiscalRua;
    const cp1 = (data.possuiEnderecoCorrespondencia ? data.enderecoCorrespondencia?.codigoPostal : fiscalCP) || fiscalCP;
    const freg1 = (data.possuiEnderecoCorrespondencia ? data.enderecoCorrespondencia?.freguesia : fiscalFreg) || fiscalFreg;
    const rua2 = fiscalRua;
    const cp2 = fiscalCP;
    const freg2 = fiscalFreg;

    // Telefones: os formulários antigos separavam prefixo e número.
    // Aqui enviamos o telefone completo em "numeroprincipal*" e deixamos prefixos vazios.
    const prefixo = '';
    const numero = data.telefone;
    const prefixo1 = '';
    const numero1 = data.perfil === 'particular' ? '' : (data.telefoneConfirmacao || '');

    const common = {
      nomeUSUARIO: data.nome,
      sobrenomeUSUARIO: data.sobrenome,
      emailUSUARIO: data.email,
      senhaUSUARIO: data.senha,
      termoecondicoesUSUARIO: !!data.aceiteTermos,
      facebookidUSUARIO: '',
      appleidUSUARIO: '',
      googleidUSUARIO: '',
      paisUSUARIO: fiscalPais,
      isentoIVA: data.isentoIVA === 'sim',
      isencaoUSUARIO: data.isentoIVA === 'sim' ? data.isencaoUSUARIO : 0,
      nif: data.nif,
      ruaenumeroUSUARIO: fiscalRua,
      codigopostalUSUARIO: fiscalCP,
      freguesiaUSUARIO: fiscalFreg,
      ruaenumero1USUARIO: rua1,
      codigopostal1USUARIO: cp1,
      freguesia1USUARIO: freg1,
      ruaenumero2USUARIO: rua2,
      codigopostal2USUARIO: cp2,
      freguesia2USUARIO: freg2,
      prefixoUSUARIO: prefixo,
      numeroprincipalUSUARIO: numero,
      prefixo1USUARIO: prefixo1,
      numeroprincipal1USUARIO: numero1,
    };

    let endpoint = '';
    let payloadFinal = {};

    if (data.perfil === 'profissional') {
      endpoint = '/usuarios-profissional/criar';
      payloadFinal = {
        ...common,
        licencaUSUARIO: data.numeroLicenca || '',
        nomeempresaUSUARIO: data.designacaoFiscal || '',
      };
    } else if (data.perfil === 'empreendimento') {
      endpoint = '/usuarios-empreendimento/criar';
      payloadFinal = {
        ...common,
        licencaUSUARIO: data.numeroLicenca || '',
        nomeempresaUSUARIO: data.designacaoFiscal || '',
      };
    } else if (data.perfil === 'particular') {
      endpoint = '/usuarios-particular/criar';
      payloadFinal = {
        ...common,
        licencaUSUARIO: data.numeroLicenca || '',
        emailContatoUSUARIO: data.emailContato || '',
      };
    } else {
      throw new Error('Tipo de perfil inválido.');
    }

    try {
      const url = `${API_BASE}${endpoint.startsWith('/') ? endpoint : `/${endpoint}`}`;

      // Debug do request
      try { if (DEBUG) console.debug('CadastroHostd request', { url, payload: payloadFinal }); } catch (_) { /* noop */ }

      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payloadFinal),
      });

      // Ler como texto e tentar JSON em seguida (evita body usado duas vezes)
      let rawText = '';
      let responseData = null;
      try { rawText = await res.text(); } catch (_) { rawText = ''; }
      try { responseData = rawText ? JSON.parse(rawText) : null; } catch (_) { /* não-JSON */ }
      // Log leve para depuração local
      try { if (DEBUG) console.debug('CadastroHostd resposta', { status: res.status, body: responseData || rawText }); } catch (_) { /* noop */ }

      if (!res.ok) {
        const fallback = 'Não foi possível criar a conta.';
        let apiMsg = responseData?.mensagem || responseData?.message || rawText || fallback;
        // Mapeia erros comuns de servidor (SMTP/Autenticação)
        const lt = (apiMsg || '').toLowerCase();
        const isSmtp = res.status === 500 && (lt.includes('535 5.7.8') || lt.includes('authentication failed') || lt.includes('smtp'));
        if (isSmtp) {
          apiMsg = 'Falha ao enviar o e-mail de confirmação. Nosso servidor de e-mail precisa de ajuste. Tente novamente mais tarde ou entre em contato com o suporte.';
        }
        const err = new Error(`${apiMsg} [HTTP ${res.status}]`);
        err.status = res.status;
        // marca erro específico de envio de e-mail
        err.isSmtp = isSmtp;
        throw err;
      }

      setSubmitMessage(`Enviamos um e-mail de confirmação para ${data.email}. Aceda à sua caixa de entrada (e também à pasta de spam) e clique no link para validar a sua conta.`);
      // Reinicia estado local da UI
      setPerfil(perfilLock || 'profissional');
      setShowPassword(false);
      setShowPasswordConfirm(false);
      // Reset explícito do formulário para os valores padrão
      reset({
        perfil: perfilLock || 'profissional',
        nome: '',
        sobrenome: '',
        email: '',
        senha: '',
        senhaConfirmacao: '',
        nif: '',
        isencaoUSUARIO: '',
        isentoIVA: 'nao',
        enderecoFiscal: { rua: '', codigoPostal: '', freguesia: '', pais: '' },
        telefone: '',
        telefoneConfirmacao: '',
        aceiteTermos: false,
        designacaoFiscal: '',
        numeroLicenca: '',
        possuiEnderecoCorrespondencia: false,
        contatoFuncionariosDiferente: false,
        telefoneFuncionarios: '',
      });
      try { window.scrollTo({ top: 0, behavior: 'smooth' }); } catch (_) { /* noop */ }
    } catch (err) {
      const msg = err?.message || 'Erro ao enviar. Tente novamente.';
      // Log do erro com contexto mínimo
      try { if (DEBUG) console.error('CadastroHostd erro', { mensagem: msg, status: err?.status, endpoint, payload: payloadFinal }); } catch (_) { /* noop */ }
      // Se já cadastrado (409), tentar identificar campo específico
      if (err?.status === 409 || /conflit|já cadastrado|conflict/i.test(msg)) {
        const lower = msg.toLowerCase();
        if (lower.includes('email')) {
          try { setError('email', { type: 'manual', message: 'Este email já está cadastrado.' }); } catch (_) { /* noop */ }
          try { setFocus('email'); } catch (_) { /* noop */ }
        } else if (lower.includes('nif')) {
          try { setError('nif', { type: 'manual', message: 'Este NIF já está cadastrado.' }); } catch (_) { /* noop */ }
          try { setFocus('nif'); } catch (_) { /* noop */ }
        } else {
          // fallback para email, mais comum
          try { setError('email', { type: 'manual', message: 'Conta já existente para os dados informados.' }); } catch (_) { /* noop */ }
        }
        // Evitar banner de erro superior; manter apenas erros de campo
        setSubmitError('');
        return;
      }
      if (err?.isSmtp) {
        // Conta criada, mas falha no envio de e-mail (SMTP 500)
        setShowEmailError(true);
        try { reset(); } catch (_) { /* noop */ }
        try { clearErrors(); } catch (_) { /* noop */ }
        setSubmitError('');
        return;
      }
      setSubmitError(msg);
      try { window.scrollTo({ top: 0, behavior: 'smooth' }); } catch (_) { /* noop */ }
    } finally {
      setSubmitting(false);
    }
  };

  const extractFirstError = (errorsObj) => {
    if (!errorsObj) return null;
    for (const key of Object.keys(errorsObj)) {
      const value = errorsObj[key];
      if (!value) continue;
      if (value.message) {
        return { name: key, message: value.message };
      }
      if (value.types) {
        const firstTypeMessage = Object.values(value.types)[0];
        if (firstTypeMessage) {
          return { name: key, message: firstTypeMessage };
        }
      }
      if (typeof value === 'object') {
        const nested = extractFirstError(value);
        if (nested) {
          return {
            name: `${key}.${nested.name}`.replace(/\.+/, '.'),
            message: nested.message,
          };
        }
      }
    }
    return null;
  };

  const onInvalidSubmit = (formErrors) => {
    try {
      if (DEBUG) {
        console.warn('CadastroHostd submissão inválida', formErrors);
      }
    } catch (_) { /* noop */ }

    const firstError = extractFirstError(formErrors);
    const fallbackMsg = 'Revise os campos destacados e tente novamente.';
    setSubmitError(firstError?.message || fallbackMsg);
    if (firstError?.name) {
      try { setFocus(firstError.name); } catch (_) { /* noop */ }
    }
    try { window.scrollTo({ top: 0, behavior: 'smooth' }); } catch (_) { /* noop */ }
  };

  return (
    <PageContainer>
      {/* Modal de sucesso centralizado */}
      {submitMessage && (
        <SuccessOverlay>
          <SuccessCard role="dialog" aria-modal="true" aria-labelledby="success-title">
            <SuccessIconWrap>
              <FiCheckCircle size={34} />
            </SuccessIconWrap>
            <SuccessTitle id="success-title">E-mail de confirmação enviado</SuccessTitle>
            <SuccessDesc>{submitMessage}</SuccessDesc>
            <SuccessActions>
              <SuccessButton onClick={() => setSubmitMessage('')}>Fechar</SuccessButton>
            </SuccessActions>
          </SuccessCard>
        </SuccessOverlay>
      )}
      {/* Modal de erro centralizado para falha no envio de e-mail */}
      {showEmailError && (
        <SuccessOverlay>
          <SuccessCard role="dialog" aria-modal="true" aria-labelledby="error-title">
            <SuccessIconWrap style={{ color: '#991b1b', background: 'radial-gradient(closest-side, #fee2e2, #fef2f2)', borderColor: '#fecaca' }}>
              <FiAlertCircle size={34} />
            </SuccessIconWrap>
            <SuccessTitle id="error-title">Não foi possível enviar o e-mail de confirmação</SuccessTitle>
            <SuccessDesc>{submitError}</SuccessDesc>
            <SuccessActions>
              <SuccessButton onClick={() => setShowEmailError(false)}>Fechar</SuccessButton>
            </SuccessActions>
          </SuccessCard>
        </SuccessOverlay>
      )}
      <Card>
        <CardHeader>
          <Title>Cadastro de Anfitrião</Title>
          {submitError && (
            <AlertBoxError style={{ marginTop: 10 }}>
              <AlertIconError>
                <FiAlertCircle size={18} />
              </AlertIconError>
              <AlertContent>
                <AlertTitle>Algo deu errado</AlertTitle>
                <AlertDesc>{submitError}</AlertDesc>
              </AlertContent>
              <AlertClose onClick={() => setSubmitError('')} aria-label="Fechar alerta">
                <FiX size={16} />
              </AlertClose>
            </AlertBoxError>
          )}
          <Tabs>
            {tabsToShow.map((p) => (
              <TabButton key={p} $active={perfil === p} onClick={() => onChangePerfil(p)}>
                {p === 'profissional' ? 'Profissional' : p === 'empreendimento' ? 'Empreendimento' : 'Particular'}
              </TabButton>
            ))}
          </Tabs>
        </CardHeader>
        <CardContent>
        <form autoComplete="off" onSubmit={handleSubmit(onSubmit, onInvalidSubmit)}>
          {/* Dados da conta */}
          <Section>
            <SectionTitle>Dados da conta</SectionTitle>
            <Grid>
              <InputGroup>
                <Label htmlFor="nome">Nome</Label>
                <Input id="nome" autoComplete="off" {...register('nome')} />
                {errors.nome && <ErrorText>{errors.nome.message}</ErrorText>}
              </InputGroup>
              <InputGroup>
                <Label htmlFor="sobrenome">Sobrenome</Label>
                <Input id="sobrenome" autoComplete="off" {...register('sobrenome')} />
                {errors.sobrenome && <ErrorText>{errors.sobrenome.message}</ErrorText>}
              </InputGroup>
              <InputGroup style={{ gridColumn: '1 / -1' }}>
                <Label htmlFor="email">Email</Label>
                <Input id="email" type="email" autoComplete="off" {...register('email')} />
                {errors.email && <ErrorText>{errors.email.message}</ErrorText>}
              </InputGroup>
              <InputGroup>
                <Label htmlFor="senha">Senha</Label>
                <PasswordWrapper>
                  <Input id="senha" type={showPassword ? 'text' : 'password'} autoComplete="new-password" {...register('senha')} />
                  <TogglePasswordButton
                    type="button"
                    aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
                    onClick={() => setShowPassword(v => !v)}
                  >
                    {showPassword ? <FiEyeOff size={18} /> : <FiEye size={18} />}
                  </TogglePasswordButton>
                </PasswordWrapper>
                {errors.senha && <ErrorText>{errors.senha.message}</ErrorText>}
              </InputGroup>
              <InputGroup>
                <Label htmlFor="senhaConfirmacao">Confirmar senha</Label>
                <PasswordWrapper>
                  <Input id="senhaConfirmacao" type={showPasswordConfirm ? 'text' : 'password'} autoComplete="new-password" {...register('senhaConfirmacao')} />
                  <TogglePasswordButton
                    type="button"
                    aria-label={showPasswordConfirm ? 'Ocultar confirmação' : 'Mostrar confirmação'}
                    onClick={() => setShowPasswordConfirm(v => !v)}
                  >
                    {showPasswordConfirm ? <FiEyeOff size={18} /> : <FiEye size={18} />}
                  </TogglePasswordButton>
                </PasswordWrapper>
                {errors.senhaConfirmacao && <ErrorText>{errors.senhaConfirmacao.message}</ErrorText>}
              </InputGroup>
            </Grid>
          </Section>

            {/* Dados da empresa - somente profissional/empreendimento */}
            {(perfil === 'profissional' || perfil === 'empreendimento') && (
              <Section>
                <SectionTitle>Dados da empresa</SectionTitle>
                <Grid>
                  <InputGroup>
                    <Label htmlFor="designacaoFiscal">Designação fiscal</Label>
                    <Input id="designacaoFiscal" {...register('designacaoFiscal')} />
                    {errors.designacaoFiscal && <ErrorText>{errors.designacaoFiscal.message}</ErrorText>}
                  </InputGroup>
                  <InputGroup>
                    <Label htmlFor="numeroLicenca">Número da licença (opcional)</Label>
                    <Input id="numeroLicenca" {...register('numeroLicenca')} />
                  </InputGroup>
                </Grid>
                
              </Section>
            )}

            {/* NIF */}
            <Section>
              <SectionTitle>NIF</SectionTitle>
              {perfil === 'particular' ? (
                <Grid>
                  <InputGroup>
                    <Label htmlFor="nif">{nifLabel}</Label>
                    <Input id="nif" {...register('nif')} />
                    {errors.nif && <ErrorText>{errors.nif.message}</ErrorText>}
                  </InputGroup>
                  <InputGroup>
                    <Label htmlFor="numeroLicenca">Número da licença (opcional)</Label>
                    <Input id="numeroLicenca" {...register('numeroLicenca')} />
                  </InputGroup>
                </Grid>
              ) : (
                <Grid>
                  <InputGroup style={{ gridColumn: '1 / -1' }}>
                    <Label htmlFor="nif">{nifLabel}</Label>
                    <Input id="nif" {...register('nif')} />
                    {errors.nif && <ErrorText>{errors.nif.message}</ErrorText>}
                  </InputGroup>
                </Grid>
              )}
            </Section>

            {/* Endereço Fiscal */}
            <Section>
              <SectionTitle>Endereço Fiscal</SectionTitle>
              <Grid>
                <InputGroup style={{ gridColumn: '1 / -1' }}>
                  <Label htmlFor="rua">Rua/Número</Label>
                  <Input id="rua" {...register('enderecoFiscal.rua')} />
                  {errors.enderecoFiscal?.rua && <ErrorText>{errors.enderecoFiscal.rua.message}</ErrorText>}
                </InputGroup>
                <InputGroup>
                  <Label htmlFor="codigoPostal">Código Postal</Label>
                  <Input id="codigoPostal" {...register('enderecoFiscal.codigoPostal')} />
                  {errors.enderecoFiscal?.codigoPostal && <ErrorText>{errors.enderecoFiscal.codigoPostal.message}</ErrorText>}
                </InputGroup>
                <InputGroup>
                  <Label htmlFor="freguesia">Freguesia</Label>
                  <Input id="freguesia" {...register('enderecoFiscal.freguesia')} />
                  {errors.enderecoFiscal?.freguesia && <ErrorText>{errors.enderecoFiscal.freguesia.message}</ErrorText>}
                </InputGroup>
                <InputGroup style={{ gridColumn: '1 / -1' }}>
                  <Label htmlFor="pais">País</Label>
                  <Select id="pais" {...register('enderecoFiscal.pais')} defaultValue="">
                    <option value="" disabled>Selecione o país</option>
                    {countries.map((p) => (
                      <option key={p.codigoPAIS} value={p.codigoPAIS}>{p.nomePAIS}</option>
                    ))}
                  </Select>
                  {errors.enderecoFiscal?.pais && <ErrorText>{errors.enderecoFiscal.pais.message}</ErrorText>}
                  <Label htmlFor="isentoIVA" style={{ marginTop: 12 }}>IVA na compra da assinatura/anúncio</Label>
                  <Select id="isentoIVA" {...register('isentoIVA')}>
                    <option value="nao">Sem Isenção de IVA</option>
                    <option value="sim">Com Isenção de IVA</option>
                  </Select>
                  <SmallNote>O país, por si só, não determina a isenção. A isenção da sua atividade não se aplica automaticamente a esta compra. Indique apenas um código de isenção aplicável à assinatura/anúncio, confirmado com o seu contabilista.</SmallNote>
                  {isentoIVA === 'sim' && (
                    <InputGroup style={{ marginTop: 12 }}>
                      <Label htmlFor="isencaoUSUARIO">Código de isenção do IVA</Label>
                      {paisSelecionado === '1' ? (
                        <>
                          <Select disabled={!isencoesFiscais.length} id="isencaoUSUARIO" {...register('isencaoUSUARIO')} defaultValue="">
                            <option value="" disabled>Selecione o código de isenção</option>
                            {isencoesFiscais.map((i) => {
                              const label = `${i.descricaoISENCAO || i.nomeISENCAO || ''}`.slice(0, 100);
                              return (
                                <option key={i.codigoISENCAO} value={i.codigoISENCAO} title={i.descricaoISENCAO || i.nomeISENCAO}>{label}</option>
                              );
                            })}
                          </Select>
                          {!isencoesFiscais.length && <ErrorText>Não há motivos disponíveis. Atualize a página ou contacte o suporte.</ErrorText>}
                        </>
                      ) : (
                        <Input id="isencaoUSUARIO" placeholder="Ex.: M01" autoComplete="off" {...register('isencaoUSUARIO')} />
                      )}
                      {errors.isencaoUSUARIO && <ErrorText>{errors.isencaoUSUARIO.message}</ErrorText>}
                    </InputGroup>
                  )}
                </InputGroup>
              </Grid>
            </Section>

            {(perfil === 'profissional' || perfil === 'empreendimento') && (
              <Section>
                <div style={{ display: 'grid', gap: 10 }}>
                  <SwitchRow>
                    <div>
                      <strong>Selecione se o endereço de correspondência for diferente</strong>
                      <div style={{ fontSize: 13, color: '#6b7280' }}></div>
                    </div>
                    <input type="checkbox" {...register('possuiEnderecoCorrespondencia')} />
                  </SwitchRow>
                  {possuiEnderecoCorrespondencia && (
                    <div style={{ padding: '8px 0 0 0' }}>
                      <Grid>
                        <InputGroup style={{ gridColumn: '1 / -1' }}>
                          <Label htmlFor="corr_rua">Rua/Número (Correspondência)</Label>
                          <Input id="corr_rua" {...register('enderecoCorrespondencia.rua')} />
                          {errors.enderecoCorrespondencia?.rua && <ErrorText>{errors.enderecoCorrespondencia.rua.message}</ErrorText>}
                        </InputGroup>
                        <InputGroup>
                          <Label htmlFor="corr_codigoPostal">Código Postal</Label>
                          <Input id="corr_codigoPostal" {...register('enderecoCorrespondencia.codigoPostal')} />
                          {errors.enderecoCorrespondencia?.codigoPostal && <ErrorText>{errors.enderecoCorrespondencia.codigoPostal.message}</ErrorText>}
                        </InputGroup>
                        <InputGroup>
                          <Label htmlFor="corr_freguesia">Freguesia</Label>
                          <Input id="corr_freguesia" {...register('enderecoCorrespondencia.freguesia')} />
                          {errors.enderecoCorrespondencia?.freguesia && <ErrorText>{errors.enderecoCorrespondencia.freguesia.message}</ErrorText>}
                        </InputGroup>
                      </Grid>
                    </div>
                  )}
                </div>
              </Section>
            )}

            {/* CONTACTOS */}
            <Section>
              <SectionTitle>CONTACTOS</SectionTitle>
              {perfil === 'particular' ? (
                <Grid>
                  <InputGroup>
                    <Label htmlFor="telefone">Número de telefone</Label>
                    <Input id="telefone" {...register('telefone')} />
                    {errors.telefone && <ErrorText>{errors.telefone.message}</ErrorText>}
                  </InputGroup>
                  <InputGroup>
                    <Label htmlFor="emailContato">E-mail</Label>
                    <Input id="emailContato" type="email" {...register('emailContato')} />
                    {errors.emailContato && <ErrorText>{errors.emailContato.message}</ErrorText>}
                  </InputGroup>
                </Grid>
              ) : (
                <Grid>
                  <InputGroup>
                    <Label htmlFor="telefone">Número de telefone</Label>
                    <Input id="telefone" {...register('telefone')} />
                    {errors.telefone && <ErrorText>{errors.telefone.message}</ErrorText>}
                  </InputGroup>
                  <InputGroup>
                    <Label htmlFor="telefoneConfirmacao">Número de telefone para publicação do anúncio ou receber alertas</Label>
                    <Input id="telefoneConfirmacao" {...register('telefoneConfirmacao')} />
                    {errors.telefoneConfirmacao && <ErrorText>{errors.telefoneConfirmacao.message}</ErrorText>}
                  </InputGroup>
                </Grid>
              )}
            </Section>

            {/* Contacto para funcionários - agora para todos os perfis, inclusive particular */}
            <Section>
              <SectionTitle>Contactos para comunicação com a Joviplanet</SectionTitle>
              <SwitchRow>
                <div>
                  <strong>Selecione se for diferente do contacto principal</strong>
                  <div style={{ fontSize: 13, color: '#6b7280' }}></div>
                </div>
                <input type="checkbox" {...register('contatoFuncionariosDiferente')} />
              </SwitchRow>
              {contatoFuncionariosDiferente && (
                <div style={{ marginTop: 10 }}>
                  <InputGroup>
                    <Label htmlFor="telefoneFuncionarios">Telefone para comunicação</Label>
                    <Input id="telefoneFuncionarios" {...register('telefoneFuncionarios')} />
                    {errors.telefoneFuncionarios && <ErrorText>{errors.telefoneFuncionarios.message}</ErrorText>}
                  </InputGroup>
                  <InputGroup>
                    <Label htmlFor="emailFuncionarios">E-mail para comunicação</Label>
                    <Input id="emailFuncionarios" type="email" {...register('emailFuncionarios')} />
                    {errors.emailFuncionarios && <ErrorText>{errors.emailFuncionarios.message}</ErrorText>}
                  </InputGroup>
                </div>
              )}
            </Section>

            {/* Termos - formato antigo (segundo print) */}
            <Section>
              <SectionTitle>Termos</SectionTitle>
              <TermsPanel $hasError={!!errors.aceiteTermos}>
                <p>
                  Ao clicar no botão <strong>Registar</strong>, aceito os{' '}
                  <TermsLink href="/terms" target="_blank" rel="noopener noreferrer">Termos & Condições</TermsLink>.
                </p>
                <p>
                  Reconheço que a JoviPlanet procede ao tratamento dos meus dados pessoais de acordo com a{' '}
                  <TermsLink href="/privacy-policy" target="_blank" rel="noopener noreferrer">Política de Privacidade</TermsLink>{' '}
                  e a{' '}
                  <TermsLink href="/cookie-policy" target="_blank" rel="noopener noreferrer">Política de Cookies e Tecnologias Similares</TermsLink>.
                </p>
                <p>
                  A JoviPlanet utiliza sistemas automatizados e parceiros para analisar como utilizo os serviços para garantir a
                  funcionalidade adequada do produto, conteúdo, publicidade personalizada e baseada em interesses, bem como
                  proteção contra spam, malware e uso não autorizado dos serviços.
                </p>

                <TermsCheckboxPanel>
                  <Controller
                    name="aceiteTermos"
                    control={control}
                    render={({ field: { value, onChange, onBlur, ref } }) => (
                      <input
                        type="checkbox"
                        checked={!!value}
                        onChange={e => onChange(e.target.checked)}
                        onBlur={onBlur}
                        ref={ref}
                      />
                    )}
                  />
                  <div>
                    <strong>Concordo com a utilização de meios de comunicação eletrónica</strong>
                    <small>
                      para fins comerciais e de marketing pela JoviPlanet e entidades parceiras. O consentimento pode ser retirado
                      a qualquer momento nas configurações da conta ou enviando um pedido através do formulário de contacto.
                    </small>
                  </div>
                </TermsCheckboxPanel>

                <SmallNote>
                  Lembramos que a aceitação dos Termos & Condições do Serviço é equivalente à celebração de um contrato.
                </SmallNote>
              </TermsPanel>
              {errors.aceiteTermos && (
                <ErrorText role="alert" style={{ marginTop: '6px' }}>
                  {errors.aceiteTermos.message}
                </ErrorText>
              )}

              <NoticePanel>
                Preocupamo-nos com o meio ambiente, por isso só emitimos faturas em formato eletrónico. Receberá um e-mail sempre que
                uma nova fatura estiver disponível na sua conta.
              </NoticePanel>
            </Section>

            <SubmitButton type="submit" disabled={submitting}>
              {submitting ? 'Enviando...' : 'Registar'}
            </SubmitButton>
          </form>
        </CardContent>
      </Card>
    </PageContainer>
  );
}
